'use client'
import { useMediaQuery } from 'react-responsive'
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn, getLocalizedPath } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import SwiperBox from '../SwiperBox/index.js'
import { Button, Heading, Picture, Text } from '../../components/index.js'
import Title from '../Title/index.js'
import { VideoModal } from '../VideoModal/index.js'
import { TextModal } from '../TextModal/index.js'
import type { ComponentCommonProps, Video, Img, Theme } from '../../types/props.js'
import { useExposure } from '../../hooks/useExposure.js'
import { trackUrlRef } from '../../shared/trackUrlRef.js'
import { useAiuiContext } from '../AiuiProvider/index.js'

const componentType = 'image'
const componentName = 'graphic'

type GraphicType = {
  imgUrl: Img
  title: string
  description?: string
  textColor?: string
  href?: string
  video?: Video
  mobileVideo?: Video
  youtubeId?: string
  isYouTube?: boolean
  isShowIcon?: boolean
  buttonText?: string
  icon?: Img
  theme?: Theme
  backgroundImage?: {
    default?: Img
    tablet?: Img
    laptop?: Img
    desktop?: Img
    lgDesktop?: Img
  }
  extension?: {
    text: string
    textDesc: string
    textTitle: string
    textPcImg: Img
    textMobileImg: Img
  }
}

/**
 * 将 backgroundImage 各尺寸字段拼接成 Picture source 字符串格式
 *
 * Picture 内部匹配规则：对每个 deviceSize，过滤 breakpoint >= deviceSize 的条目，取最后一个 (.pop())。
 * 因此 source 字符串内断点必须「由大到小」排列，才能让 .pop() 匹配到最接近自身的断点。
 *
 * 断点对应关系 (max-width):
 *   lgDesktop → 1919px  (≤1920px, 充当最大屏屏幕匹配层)
 *   desktop  → 1439px  (1440px - 1919px)
 *   laptop   → 1024px  (1025px - 1439px)
 *   tablet   → 767px   (768px - 1024px)
 *   default  → 首位无断点，充当 <img src> (移动端 <768px 屏幕)
 *
 * 继承规则（大 → 小）：未配置的尺寸自动继承上一尺寸，最终 fallback 到 default
 */
const buildPictureSource = (bg: NonNullable<GraphicType['backgroundImage']>): string => {
  // 由大到小继承：未配置的尺寸继承上一尺寸
  let last = ''
  const resolve = (img?: Img) => {
    if (img?.url) last = img.url
    return last
  }
  const lgDesktopSrc = resolve(bg.lgDesktop)
  const desktopSrc = resolve(bg.desktop)
  const laptopSrc = resolve(bg.laptop)
  const tabletSrc = resolve(bg.tablet)
  // default 无断点，作为首位充当 <img src>(移动端屏幕)
  const defaultSrc = bg.default?.url
  return [
    `${lgDesktopSrc} 9999`, // ≤1920px
    `${desktopSrc} 1919`, // 1440-1919px
    `${laptopSrc} 1439`, // 1025-1439px
    `${tabletSrc} 1024`, // 768-1024px
    `${defaultSrc} 768`, // 首位无断点 → <img src>(<768px)
  ].join(', ')
}
const descriptionClass =
  'descTitle lg-desktop:text-[32px] desktop:mt-2 desktop:text-[24px] text-lines-2 mt-1 line-clamp-3 flex-1 text-[24px] font-bold leading-[1.2]'
export interface GraphicProps extends ComponentCommonProps, React.HTMLAttributes<HTMLDivElement> {
  data: {
    title?: string
    items?: GraphicType[]
    itemShape?: 'round' | 'square'
    itemTheme?: Theme
  }
}

const Item = ({ data, configuration }: { data: GraphicType; configuration?: any }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number; text: string }>({
    visible: false,
    x: 0,
    y: 0,
    text: '',
  })

  const handleAspect = () => {
    switch (configuration.num) {
      case 1:
        return 'tablet:aspect-w-[704] tablet:aspect-h-[400] laptop:aspect-w-[896] laptop:aspect-h-[384] desktop:aspect-w-[1312] desktop:aspect-h-[512] lg-desktop:aspect-w-[1664] lg-desktop:aspect-h-[640]'
      case 2:
        return 'tablet:aspect-w-[346] tablet:aspect-h-[360] laptop:aspect-w-[440] laptop:aspect-h-[360] desktop:aspect-w-[648] desktop:aspect-h-[384] lg-desktop:aspect-w-[824] lg-desktop:aspect-h-[480]'
      case 3:
        return 'tablet:aspect-w-[296] tablet:aspect-h-[360] laptop:aspect-w-[288] laptop:aspect-h-[360] desktop:aspect-w-[427] desktop:aspect-h-[384] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[480]'
      default:
        return 'tablet:aspect-w-[296] tablet:aspect-h-[360] laptop:aspect-w-[288] laptop:aspect-h-[360] desktop:aspect-w-[316] desktop:aspect-h-[384] lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[480]'
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (data?.description) {
      setTooltip({
        visible: true,
        x: e.clientX,
        y: e.clientY,
        text: data.description,
      })
    }
  }

  const handleMouseLeave = () => {
    setTooltip(prev => ({ ...prev, visible: false }))
  }

  useExposure(ref, {
    componentType,
    componentName,
    componentTitle: data?.title,
    componentDescription: data?.description,
    position: configuration?.index + 1,
  })

  return (
    <div
      ref={ref}
      className={cn(
        handleAspect(),
        'graphicItem',
        'laptop:h-auto text-info-primary md-tablet:h-[360px] flex-1 shrink-0 md:basis-[296px]',
        {
          'aiui-dark': data.theme === 'dark',
        }
      )}
    >
      <div
        className={cn('absolute inset-0', {
          ['rounded-xl overflow-hidden laptop:rounded-2xl']: configuration?.itemShape === 'round',
        })}
      >
        <div
          className={`graphic-children relative block size-full overflow-hidden ${data?.href ? 'cursor-pointer' : ''}`}
        >
          {data?.href ? (
            <a
              aria-label={data?.title ?? data?.description}
              href={trackUrlRef(data?.href || '', `${componentType}_${componentName}`)}
              className="rounded-card block size-full overflow-hidden"
              data-headless-nav-postion={`''#${configuration?.index + 1}`}
              data-headless-type-name={`${componentType}#${componentName}`}
              data-headless-title-desc-button={`${data?.title}#${data?.description}`}
            >
              <Picture
                className="h-full"
                imgClassName="h-full object-cover tablet:hover:scale-110 transition-all duration-300"
                source={data?.backgroundImage?.default ? buildPictureSource(data.backgroundImage) : data?.imgUrl?.url}
                alt={
                  data?.backgroundImage?.lgDesktop?.alt ||
                  data?.backgroundImage?.default?.alt ||
                  data?.imgUrl?.alt ||
                  ''
                }
              />
            </a>
          ) : (
            <div className="rounded-card block size-full overflow-hidden">
              <Picture
                className="h-full"
                imgClassName="h-full object-cover tablet:hover:scale-110 transition-all duration-300"
                source={
                  data?.backgroundImage?.default ? buildPictureSource(data.backgroundImage) : data?.imgUrl?.url || ''
                }
                alt={
                  data?.backgroundImage?.lgDesktop?.alt ||
                  data?.backgroundImage?.default?.alt ||
                  data?.imgUrl?.alt ||
                  ''
                }
              />
            </div>
          )}
          <div className="laptop:p-6 absolute bottom-0 z-[1] box-border flex w-full flex-col p-4">
            <div className={cn('flex items-end gap-1 overflow-hidden', 'graphic-bottom')}>
              <div className="flex-1 overflow-hidden">
                <Text
                  html={data?.title}
                  style={{ color: data?.textColor }}
                  className="spanTitle graphic-title lg-desktop:text-lg desktop:text-base line-clamp-3 text-sm font-bold leading-[1.2]"
                />
                {data?.description && (
                  <div onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
                    {data?.title ? (
                      <Heading
                        html={data?.description}
                        style={{
                          color: data?.textColor,
                        }}
                        as="h3"
                        className={`${descriptionClass} graphic-description`}
                      />
                    ) : (
                      <h3
                        style={{
                          color: data?.textColor,
                        }}
                        dangerouslySetInnerHTML={{ __html: data?.description || '' }}
                        className={`${descriptionClass} graphic-description-item`}
                      />
                    )}
                    {typeof window !== 'undefined' &&
                      tooltip.visible &&
                      createPortal(
                        <div
                          className="pointer-events-none fixed z-[9999] max-w-xs rounded-lg bg-black/60 px-3 py-2 text-sm text-white shadow-lg backdrop-blur-sm transition-opacity duration-200"
                          style={{
                            left: `${tooltip.x + 16}px`,
                            top: `${tooltip.y + 16}px`,
                          }}
                        >
                          {tooltip.text}
                        </div>,
                        document.body
                      )}
                  </div>
                )}
              </div>
              {!data?.isShowIcon && (data?.video?.url || data?.youtubeId) && (
                <button
                  aria-label="Play Video"
                  onClick={(e: any) => {
                    e.preventDefault()
                    e.stopPropagation()
                    configuration?.onVideoPlayBtnClick?.(configuration?.index || 0, data)
                  }}
                  className="laptop:size-12 flex size-8 items-center justify-center rounded-full bg-white/20 hover:bg-black/75"
                >
                  <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 7L0 14L0 0L12 7Z" fill="white" />
                  </svg>
                </button>
              )}
              {data?.isShowIcon && data?.icon?.url && (
                <button
                  onClick={(e: any) => {
                    e.preventDefault()
                    e.stopPropagation()
                    configuration?.onIconClick?.(data, configuration)
                  }}
                  className="laptop:size-9 flex size-8 items-center justify-center overflow-hidden rounded-full bg-white/20 hover:bg-black/75"
                >
                  <Picture
                    className="h-full"
                    imgClassName="h-full object-cover"
                    source={data?.icon?.url}
                    alt={data?.icon?.alt || ''}
                  />
                </button>
              )}
            </div>
            {data.buttonText && (
              <Button
                as="a"
                aria-label={data?.title ?? data?.description}
                variant="secondary"
                className="bgButton mt-[8px] self-start"
                href={trackUrlRef(
                  getLocalizedPath(data?.href || '', configuration?.locale || 'us'),
                  `${componentType}_${componentName}`
                )}
                data-headless-nav-position={`''#${configuration?.index + 1}`}
                data-headless-type-name={`${componentType}#${componentName}`}
                data-headless-title-desc-button={`${data?.title}#${data?.description}#${data?.buttonText}`}
              >
                {data.buttonText}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const Graphic = React.forwardRef<HTMLDivElement, GraphicProps>(({ data, className, ...rest }, ref) => {
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' })
  const innerRef = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState<boolean>(false)
  const [videoUrl, setVideoUrl] = useState<string>('')
  const [youTubeId, setYouTubeId] = useState<string>('')
  const [mobileVideoUrl, setMobileVideoUrl] = useState<string>('')
  const [currentItem, setCurrentItem] = useState<GraphicType | null>(null)
  const [textVisible, setTextVisible] = useState<boolean>(false)
  const { locale = 'us' } = useAiuiContext()

  const handleSwiperShow = (width?: number) => {
    const isShow = (data?.items as GraphicType[])?.length > 3
    const isMobile = (data?.items as GraphicType[])?.length > 2
    switch (width) {
      case 1440:
        return isShow ? 4 : data?.items?.length
      case 1024:
        return isShow ? 3 : data?.items?.length
      case 768:
        if ((data?.items as GraphicType[])?.length === 1) return 1 // ✅ 新增：只有一个 item 时，展示 1 个
        return isShow ? 2.3 : isMobile ? 2.3 : 2
      default:
        if ((data?.items as GraphicType[])?.length === 1) return 1 // ✅ 新增：只有一个 item 时，展示 1 个
        return 1.2
    }
  }

  useImperativeHandle(ref, () => innerRef.current as HTMLDivElement)

  useExposure(innerRef, {
    componentType,
    componentName,
    componentTitle: data?.title,
  })

  return (
    <div {...rest} ref={innerRef} className={cn('graphicBlock', className)}>
      <div className="graphic-box">
        {data?.title && <Title data={{ title: data?.title }} />}
        <SwiperBox
          id="Graphic"
          className={cn('!overflow-visible')}
          data={{
            list: data?.items || [],
            configuration: {
              itemShape: data.itemShape,
              num: data?.items?.length || 0,
              locale,
              onVideoPlayBtnClick: (_: number, data: GraphicType) => {
                setVisible(true)
                if (data?.isYouTube) {
                  setYouTubeId?.(data?.youtubeId || '')
                } else {
                  setVideoUrl?.(data?.video?.url || '')
                  setMobileVideoUrl?.(data?.mobileVideo?.url || '')
                }
              },
              onIconClick: (data: GraphicType) => {
                setTextVisible(true)
                setCurrentItem(data)
              },
              title: data?.title,
            },
          }}
          Slide={Item}
          breakpoints={{
            0: {
              spaceBetween: 12,
              freeMode: false,
              slidesPerView: 1,
            },
            374: {
              spaceBetween: 12,
              freeMode: false,
              slidesPerView: handleSwiperShow(),
            },
            768: {
              spaceBetween: 16,
              freeMode: false,
              slidesPerView: handleSwiperShow(768),
            },
            1024: {
              spaceBetween: 16,
              freeMode: false,
              slidesPerView: handleSwiperShow(1024),
            },
            1440: {
              spaceBetween: 16,
              freeMode: false,
              slidesPerView: handleSwiperShow(1440),
            },
          }}
        />
      </div>
      <VideoModal
        visible={visible}
        youTubeId={youTubeId}
        onCloseModal={() => setVisible(false)}
        videoUrl={isMobile ? mobileVideoUrl || videoUrl : videoUrl}
      />
      <TextModal
        textVisible={textVisible}
        extension={currentItem?.extension}
        onCloseModal={() => {
          setTextVisible(false)
          setCurrentItem(null)
        }}
      />
    </div>
  )
})

Graphic.displayName = 'Graphic'

export default withLayout(Graphic)
